import { FormEvent, KeyboardEvent, useEffect, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { SESSION_EXPIRED_KEY, useAuth } from "@/auth/AuthContext";
import { BrandLockup } from "@/components/Brand";
import { ApiError } from "@/api/client";

/** Every user-visible string on the sign-in door, in one place — the
 *  wording gets edited far more often than the layout. */
const CONTENT = {
  headline: ["Every listing, every portal,", "one console."],
  sub: "Publishing, market intel and deal flow for ss.ge and myhome.ge — in one place.",
  mobileTagline: "Every listing, every portal — one console.",
  title: "Sign in",
  welcome: "Welcome back.",
  expired: "Your session expired — please sign in again.",
  domain: "console.meridianagency.ge",
  inviteOnly: "Invite-only access — ask your manager for an invite.",
  portalsLabel: "Connected portals",
};

export function Login() {
  const { user, login, loading } = useAuth();
  const nav = useNavigate();
  const loc = useLocation() as { state?: { from?: string } };
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [capsOn, setCapsOn] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // The notice explains this visit only. Read in a *pure* initializer and
  // clear in an effect — StrictMode double-invokes initializers, so clearing
  // inside one would consume the flag on the render React throws away.
  const [expired] = useState(() => {
    try {
      return sessionStorage.getItem(SESSION_EXPIRED_KEY) === "1";
    } catch {
      return false;
    }
  });
  useEffect(() => {
    try {
      sessionStorage.removeItem(SESSION_EXPIRED_KEY);
    } catch {
      /* private mode — nothing to clear */
    }
  }, []);

  if (!loading && user) {
    return <Navigate to={loc.state?.from || "/home"} replace />;
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    setBusy(true);
    try {
      await login(email.trim().toLowerCase(), password);
      nav(loc.state?.from || "/home", { replace: true });
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) {
        setErr("Incorrect email or password.");
      } else if (error instanceof Error) {
        setErr(error.message);
      } else {
        setErr("Login failed.");
      }
    } finally {
      setBusy(false);
    }
  }

  // Caps Lock silently eats more sign-ins than a wrong password does.
  function trackCaps(e: KeyboardEvent<HTMLInputElement>) {
    setCapsOn(e.getModifierState?.("CapsLock") ?? false);
  }

  return (
    <div className="mrd-page min-h-screen lg:grid lg:grid-cols-[1.05fr_1fr]">
      {/* ── Brand panel (lg+) ───────────────────────────────────────── */}
      <aside className="mrd-panel mrd-panel-art hidden lg:flex flex-col justify-between p-12 xl:p-16">
        <span className="mrd-noise" />

        <div className="relative">
          <BrandLockup sizeClass="text-[26px]" colorClass="text-white" />
        </div>

        <div className="relative">
          {/* Wide enough at xl that the first clause sits on one line; at lg
              the panel is narrower and it wraps, which is fine. */}
          <h2 className="max-w-[40rem] text-[32px] xl:text-[40px] leading-[1.1] font-bold tracking-[-0.02em] text-[#f4f5f8]">
            {CONTENT.headline[0]}
            <br />
            <span className="text-[#5a8dee]">{CONTENT.headline[1]}</span>
          </h2>
          <p className="mt-5 max-w-[32rem] text-[14.5px] leading-relaxed text-[#9a9aa8]">
            {CONTENT.sub}
          </p>
        </div>

        <div className="relative">
          <div className="h-px w-full bg-[rgba(255,255,255,0.08)]" />
          <div className="mt-5 flex flex-wrap items-center justify-between gap-x-6 gap-y-3">
            {/* Portal wordmarks set in type rather than the shipped logo
                files — those are full-colour marks that go muddy once
                greyed down to this weight. */}
            <div className="flex items-center gap-3">
              <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[#55555f] whitespace-nowrap">
                {CONTENT.portalsLabel}
              </span>
              {["ss.ge", "myhome.ge"].map((p) => (
                <span
                  key={p}
                  className="text-[11.5px] font-medium text-[#8a8a97] px-2 py-0.5 rounded border border-[rgba(255,255,255,0.08)]"
                >
                  {p}
                </span>
              ))}
            </div>
            <span className="text-[11px] text-[#55555f] whitespace-nowrap">
              © {new Date().getFullYear()} Meridian
            </span>
          </div>
        </div>
      </aside>

      {/* ── Mobile brand band ───────────────────────────────────────── */}
      <div className="mrd-panel mrd-panel-art lg:hidden px-6 pt-10 pb-8">
        <span className="mrd-noise" />
        <div className="relative">
          <BrandLockup sizeClass="text-[20px]" colorClass="text-white" />
          <p className="mt-3 text-[15px] leading-snug font-medium text-[#c8ccd6] max-w-[20rem]">
            {CONTENT.mobileTagline}
          </p>
        </div>
      </div>

      {/* ── Form ────────────────────────────────────────────────────── */}
      <main className="flex items-center justify-center px-6 py-12 sm:py-16">
        <div className="w-full max-w-[23rem]">
          <h1 className="text-[26px] font-bold tracking-[-0.01em] text-[#f4f5f8]">
            {CONTENT.title}
          </h1>
          <p className="mt-1.5 text-[14px] text-[#9a9aa8]">{CONTENT.welcome}</p>

          {expired && (
            <div className="mt-6 rounded-[10px] border border-[rgba(90,141,238,0.28)] bg-[rgba(90,141,238,0.10)] px-3 py-2.5 text-[12.5px] text-[#a9c2f5]">
              {CONTENT.expired}
            </div>
          )}

          <form onSubmit={onSubmit} className="mt-8 space-y-4">
            <div>
              <label
                htmlFor="login-email"
                className="block text-[11px] font-semibold uppercase tracking-[0.1em] text-[#75757f] mb-1.5"
              >
                Email
              </label>
              <input
                id="login-email"
                type="email"
                autoComplete="email"
                autoFocus
                required
                aria-invalid={!!err}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mrd-input"
                placeholder="you@meridianagency.ge"
              />
            </div>

            <div>
              <label
                htmlFor="login-password"
                className="block text-[11px] font-semibold uppercase tracking-[0.1em] text-[#75757f] mb-1.5"
              >
                Password
              </label>
              <div className="relative">
                <input
                  id="login-password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  required
                  aria-invalid={!!err}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onKeyUp={trackCaps}
                  onKeyDown={trackCaps}
                  onBlur={() => setCapsOn(false)}
                  className="mrd-input !pr-11"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-pressed={showPassword}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  tabIndex={-1}
                  className="mrd-ghost absolute right-1.5 top-1/2 -translate-y-1/2 h-9 w-9 inline-flex items-center justify-center"
                >
                  {showPassword ? (
                    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M10.6 10.6a2 2 0 0 0 2.8 2.8" />
                      <path d="M16.7 16.7A9.5 9.5 0 0 1 12 18c-5 0-9-6-9-6a17 17 0 0 1 4.1-4.7m3.2-1.1A9.5 9.5 0 0 1 12 6c5 0 9 6 9 6a17 17 0 0 1-2.2 2.9" />
                      <path d="m3 3 18 18" />
                    </svg>
                  ) : (
                    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M3 12s4-6 9-6 9 6 9 6-4 6-9 6-9-6-9-6Z" />
                      <circle cx="12" cy="12" r="2.5" />
                    </svg>
                  )}
                </button>
              </div>
              {capsOn && (
                <p className="mt-1.5 text-[11.5px] text-[#e0b357]">Caps Lock is on</p>
              )}
            </div>

            {err && (
              <div className="mrd-alert" role="alert">
                {err}
              </div>
            )}

            <button
              type="submit"
              disabled={busy || !email || !password}
              className="mrd-btn inline-flex items-center justify-center gap-2"
            >
              {busy && (
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" className="animate-spin">
                  <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="3" opacity="0.25" />
                  <path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
                </svg>
              )}
              {busy ? "Signing in…" : "Sign in"}
            </button>
          </form>

          <div className="mt-10 space-y-1">
            <p className="text-[11.5px] text-[#65656f]">{CONTENT.inviteOnly}</p>
            <p className="text-[11.5px] text-[#4d4d57] mono">{CONTENT.domain}</p>
          </div>
        </div>
      </main>
    </div>
  );
}
